import React, { useState } from 'react';
import { formatCurrency } from '../utils/currencyFormatter';
import { formatDate } from '../utils/dateFormatter';
import { TruckIcon, PlusIcon, SearchIcon, FilterIcon, ArrowDownIcon, ArrowUpIcon, EyeIcon, CalendarIcon, UserIcon, ClipboardListIcon, XIcon, FileTextIcon, PackageIcon, ShoppingCartIcon, CheckCircleIcon, AlertTriangleIcon, ClockIcon, LinkIcon } from 'lucide-react';
export function ReceiptList({
  onCreateReceipt,
  onViewReceiptDetails,
  locale = 'en-US'
}) {
  const [searchQuery, setSearchQuery] = useState('');
  const [sortField, setSortField] = useState('date');
  const [sortDirection, setSortDirection] = useState('desc');
  const [filterStatus, setFilterStatus] = useState('all');
  const [selectedReceipt, setSelectedReceipt] = useState(null);
  const [showDetailsModal, setShowDetailsModal] = useState(false);
  // Sample data - in a real app this would come from your backend
  const sampleReceipts = [{
    id: 'GR-2023-001',
    date: '2023-11-05',
    receivedBy: 'John Smith',
    status: 'Complete',
    totalValue: 3000.0,
    currency: 'USD',
    purchaseOrders: [{
      poId: 'PO-2023-001',
      poName: 'Server Hardware',
      vendor: 'Server Solutions Inc.',
      items: [{
        id: 'ITEM-001',
        description: 'Dell XPS 15 Laptop',
        itemCode: 'LPT-001',
        quantity: 2,
        uom: 'EA',
        unitPrice: 1500.0,
        value: 3000.0,
        poQuantity: 5,
        remainingQuantity: 3,
        notes: 'Items received in good condition'
      }]
    }],
    deliveryDockets: [{
      id: 'DD-001-001',
      name: 'Delivery_Note_12345.pdf',
      uploadDate: '2023-11-05',
      uploadedBy: 'John Smith'
    }],
    notes: 'Partial delivery of laptops. Remaining units expected next week.',
    entity: 'Corporate',
    site: 'Headquarters',
    deliveryAddress: '123 Corporate Drive, Business Park, CA 94105',
    carrierInfo: 'FedEx',
    trackingNumber: '7891234567',
    inspectedBy: 'Alice Johnson',
    qualityCheck: 'Passed',
    inspectionDate: '2023-11-05'
  }, {
    id: 'GR-2023-002',
    date: '2023-11-10',
    receivedBy: 'Jane Doe',
    status: 'Partial',
    totalValue: 1250.0,
    currency: 'USD',
    purchaseOrders: [{
      poId: 'PO-2023-001',
      poName: 'Server Hardware',
      vendor: 'Server Solutions Inc.',
      items: [{
        id: 'ITEM-002',
        description: 'IT Setup and Configuration Services',
        itemCode: 'SVC-001',
        quantity: 0.5,
        uom: 'LOT',
        unitPrice: 2500.0,
        value: 1250.0,
        poQuantity: 1,
        remainingQuantity: 0.5,
        notes: 'Initial setup completed, final configuration pending'
      }]
    }],
    deliveryDockets: [],
    notes: 'Service partially completed. Final configuration scheduled for next month.',
    entity: 'Corporate',
    site: 'Headquarters',
    deliveryAddress: '123 Corporate Drive, Business Park, CA 94105',
    carrierInfo: 'N/A - Service',
    trackingNumber: 'N/A',
    inspectedBy: 'Robert Chen',
    qualityCheck: 'In Progress',
    inspectionDate: '2023-11-10'
  }, {
    id: 'GR-2023-003',
    date: '2023-10-10',
    receivedBy: 'Mark Johnson',
    status: 'Partial',
    totalValue: 750.0,
    currency: 'USD',
    purchaseOrders: [{
      poId: 'PO-2023-002',
      poName: 'Marketing Materials',
      vendor: 'PrintWorks Inc.',
      items: [{
        id: 'ITEM-003',
        description: 'Company Brochures',
        itemCode: 'BRO-001',
        quantity: 500,
        uom: 'EA',
        unitPrice: 1.5,
        value: 750.0,
        poQuantity: 1000,
        remainingQuantity: 500,
        notes: 'First batch of brochures received'
      }]
    }],
    deliveryDockets: [{
      id: 'DD-003-001',
      name: 'PrintWorks_Delivery_56789.pdf',
      uploadDate: '2023-10-10',
      uploadedBy: 'Mark Johnson'
    }],
    notes: 'First batch of marketing materials. Quality is excellent.',
    entity: 'Marketing',
    site: 'Headquarters',
    deliveryAddress: '123 Corporate Drive, Business Park, CA 94105',
    carrierInfo: 'UPS',
    trackingNumber: '1234567890',
    inspectedBy: 'Sarah Williams',
    qualityCheck: 'Passed',
    inspectionDate: '2023-10-10'
  }, {
    id: 'GR-2023-004',
    date: '2023-10-12',
    receivedBy: 'Mike Johnson',
    status: 'Complete',
    totalValue: 3950.5,
    currency: 'USD',
    purchaseOrders: [{
      poId: 'PO-2023-002',
      poName: 'Marketing Materials',
      vendor: 'PrintWorks Inc.',
      items: [{
        id: 'ITEM-004',
        description: 'Company Brochures',
        itemCode: 'BRO-001',
        quantity: 500,
        uom: 'EA',
        unitPrice: 1.5,
        value: 750.0,
        poQuantity: 1000,
        remainingQuantity: 0,
        notes: 'Final batch of brochures received'
      }, {
        id: 'ITEM-005',
        description: 'Banners',
        itemCode: 'BAN-001',
        quantity: 5,
        uom: 'EA',
        unitPrice: 340.1,
        value: 1700.5,
        poQuantity: 5,
        remainingQuantity: 0,
        notes: 'All banners received in good condition'
      }]
    }, {
      poId: 'PO-2023-005',
      poName: 'Trade Show Materials',
      vendor: 'PrintWorks Inc.',
      items: [{
        id: 'ITEM-006',
        description: 'Display Stands',
        itemCode: 'DSP-001',
        quantity: 3,
        uom: 'EA',
        unitPrice: 500.0,
        value: 1500.0,
        poQuantity: 3,
        remainingQuantity: 0,
        notes: 'Display stands arrived in good condition'
      }]
    }],
    deliveryDockets: [{
      id: 'DD-004-001',
      name: 'PrintWorks_Delivery_56790.pdf',
      uploadDate: '2023-10-12',
      uploadedBy: 'Mike Johnson'
    }, {
      id: 'DD-004-002',
      name: 'PrintWorks_TradeShow_Delivery_78901.pdf',
      uploadDate: '2023-10-12',
      uploadedBy: 'Mike Johnson'
    }],
    notes: 'Combined delivery of marketing materials and trade show equipment from PrintWorks. All items received in good condition.',
    entity: 'Marketing',
    site: 'Headquarters',
    deliveryAddress: '123 Corporate Drive, Business Park, CA 94105',
    carrierInfo: 'UPS',
    trackingNumber: '0987654321',
    inspectedBy: 'Sarah Williams',
    qualityCheck: 'Passed',
    inspectionDate: '2023-10-12'
  }, {
    id: 'GR-2023-005',
    date: '2023-10-15',
    receivedBy: 'Robert Chen',
    status: 'Complete',
    totalValue: 4750.0,
    currency: 'USD',
    purchaseOrders: [{
      poId: 'PO-2023-007',
      poName: 'Office Computers',
      vendor: 'Tech Solutions Inc.',
      items: [{
        id: 'ITEM-007',
        description: 'Desktop Computer',
        itemCode: 'DTC-001',
        quantity: 2,
        uom: 'EA',
        unitPrice: 1200.0,
        value: 2400.0,
        poQuantity: 2,
        remainingQuantity: 0,
        notes: 'Computers arrived in perfect condition'
      }]
    }, {
      poId: 'PO-2023-008',
      poName: 'Office Peripherals',
      vendor: 'Tech Solutions Inc.',
      items: [{
        id: 'ITEM-008',
        description: 'Monitor 27"',
        itemCode: 'MON-001',
        quantity: 3,
        uom: 'EA',
        unitPrice: 350.0,
        value: 1050.0,
        poQuantity: 3,
        remainingQuantity: 0,
        notes: 'All monitors in good condition'
      }, {
        id: 'ITEM-009',
        description: 'Keyboard and Mouse Set',
        itemCode: 'KMS-001',
        quantity: 5,
        uom: 'SET',
        unitPrice: 60.0,
        value: 300.0,
        poQuantity: 5,
        remainingQuantity: 0,
        notes: 'All peripherals working correctly'
      }]
    }, {
      poId: 'PO-2023-009',
      poName: 'Office Network Equipment',
      vendor: 'Tech Solutions Inc.',
      items: [{
        id: 'ITEM-010',
        description: 'Network Switch',
        itemCode: 'NSW-001',
        quantity: 1,
        uom: 'EA',
        unitPrice: 1000.0,
        value: 1000.0,
        poQuantity: 1,
        remainingQuantity: 0,
        notes: 'Network switch installed and tested'
      }]
    }],
    deliveryDockets: [{
      id: 'DD-005-001',
      name: 'TechSolutions_Delivery_12345.pdf',
      uploadDate: '2023-10-15',
      uploadedBy: 'Robert Chen'
    }],
    notes: 'Complete delivery of all IT equipment from Tech Solutions Inc. All items tested and working correctly.',
    entity: 'IT Department',
    site: 'Headquarters',
    deliveryAddress: '123 Corporate Drive, Business Park, CA 94105',
    carrierInfo: 'DHL',
    trackingNumber: '123456789012',
    inspectedBy: 'Robert Chen',
    qualityCheck: 'Passed',
    inspectionDate: '2023-10-15'
  }];
  // Get primary vendor for display in the table
  const getPrimaryVendor = receipt => {
    if (!receipt.purchaseOrders || receipt.purchaseOrders.length === 0) {
      return 'N/A';
    }
    return receipt.purchaseOrders[0].vendor;
  };
  // Get primary PO for display in the table
  const getPrimaryPO = receipt => {
    if (!receipt.purchaseOrders || receipt.purchaseOrders.length === 0) {
      return 'N/A';
    }
    return receipt.purchaseOrders[0].poId;
  };
  // Get all items from all POs
  const getAllItems = receipt => {
    if (!receipt.purchaseOrders) return [];
    let allItems = [];
    receipt.purchaseOrders.forEach(po => {
      if (po.items) {
        allItems = [...allItems, ...po.items.map(item => ({
          ...item,
          poId: po.poId,
          poName: po.poName,
          vendor: po.vendor
        }))];
      }
    });
    return allItems;
  };
  // Filter receipts based on search query and status filter
  const filteredReceipts = sampleReceipts.filter(receipt => {
    const matchesSearch = receipt.id.toLowerCase().includes(searchQuery.toLowerCase()) || receipt.purchaseOrders && receipt.purchaseOrders.some(po => po.poId.toLowerCase().includes(searchQuery.toLowerCase()) || po.poName.toLowerCase().includes(searchQuery.toLowerCase()) || po.vendor.toLowerCase().includes(searchQuery.toLowerCase()));
    if (filterStatus === 'all') {
      return matchesSearch;
    }
    return matchesSearch && receipt.status.toLowerCase() === filterStatus.toLowerCase();
  });
  // Sort receipts
  const sortedReceipts = [...filteredReceipts].sort((a, b) => {
    let aValue = a[sortField];
    let bValue = b[sortField];
    // Handle special case for date
    if (sortField === 'date') {
      aValue = new Date(a.date).getTime();
      bValue = new Date(b.date).getTime();
    }
    // Handle sorting by vendor or PO
    else if (sortField === 'vendor') {
      aValue = getPrimaryVendor(a);
      bValue = getPrimaryVendor(b);
    } else if (sortField === 'poId') {
      aValue = getPrimaryPO(a);
      bValue = getPrimaryPO(b);
    }
    if (aValue < bValue) {
      return sortDirection === 'asc' ? -1 : 1;
    }
    if (aValue > bValue) {
      return sortDirection === 'asc' ? 1 : -1;
    }
    return 0;
  });
  // Handle sort click
  const handleSortClick = field => {
    if (sortField === field) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('asc');
    }
  };
  // Render sort indicator
  const renderSortIndicator = field => {
    if (sortField !== field) return null;
    return sortDirection === 'asc' ? <ArrowUpIcon className="h-4 w-4 ml-1" data-id="element-13362" /> : <ArrowDownIcon className="h-4 w-4 ml-1" data-id="element-13363" />;
  };
  // Handle view receipt details
  const handleViewReceipt = receipt => {
    setSelectedReceipt(receipt);
    setShowDetailsModal(true);
    // Comment out the parent handler call to prevent the alert
    // This was causing the error when clicking View
    // if (onViewReceiptDetails) {
    //   onViewReceiptDetails(receipt)
    // }
  };
  // Close the details modal
  const closeDetailsModal = () => {
    setShowDetailsModal(false);
    setSelectedReceipt(null);
  };
  return <div className="space-y-6" data-id="element-13364">
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4" data-id="element-13365">
        <div className="w-full sm:w-96" data-id="element-13366">
          <div className="relative rounded-md shadow-sm" data-id="element-13367">
            <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none" data-id="element-13368">
              <SearchIcon className="h-5 w-5 text-gray-400" data-id="element-13369" />
            </div>
            <input type="text" className="focus:ring-indigo-500 focus:border-indigo-500 block w-full pl-10 sm:text-sm border-gray-300 rounded-md" placeholder="Search by GR#, PO#, or vendor" value={searchQuery} onChange={e => setSearchQuery(e.target.value)} data-id="element-13370" />
          </div>
        </div>
        <div className="flex flex-wrap gap-2 w-full sm:w-auto" data-id="element-13371">
          <div className="relative inline-block text-left" data-id="element-13372">
            <select value={filterStatus} onChange={e => setFilterStatus(e.target.value)} className="inline-flex justify-center w-full rounded-md border border-gray-300 shadow-sm px-4 py-2 bg-white text-sm font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500" data-id="element-13373">
              <option value="all" data-id="element-13374">All Statuses</option>
              <option value="complete" data-id="element-13375">Complete</option>
              <option value="partial" data-id="element-13376">Partial</option>
            </select>
          </div>
          <button onClick={() => onCreateReceipt()} className="inline-flex items-center px-4 py-2 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500" data-id="element-13377">
            <PlusIcon className="h-4 w-4 mr-2" data-id="element-13378" />
            Create New Receipt
          </button>
        </div>
      </div>
      <div className="bg-white shadow overflow-hidden sm:rounded-md" data-id="element-13379">
        <div className="overflow-x-auto" data-id="element-13380">
          <table className="min-w-full divide-y divide-gray-200" data-id="element-13381">
            <thead className="bg-gray-50" data-id="element-13382">
              <tr data-id="element-13383">
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer" onClick={() => handleSortClick('id')} data-id="element-13384">
                  <div className="flex items-center" data-id="element-13385">
                    Receipt # {renderSortIndicator('id')}
                  </div>
                </th>
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer" onClick={() => handleSortClick('poId')} data-id="element-13386">
                  <div className="flex items-center" data-id="element-13387">
                    PO # {renderSortIndicator('poId')}
                  </div>
                </th>
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer" onClick={() => handleSortClick('vendor')} data-id="element-13388">
                  <div className="flex items-center" data-id="element-13389">
                    Vendor {renderSortIndicator('vendor')}
                  </div>
                </th>
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer" onClick={() => handleSortClick('date')} data-id="element-13390">
                  <div className="flex items-center" data-id="element-13391">
                    Date {renderSortIndicator('date')}
                  </div>
                </th>
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer" onClick={() => handleSortClick('receivedBy')} data-id="element-13392">
                  <div className="flex items-center" data-id="element-13393">
                    Received By {renderSortIndicator('receivedBy')}
                  </div>
                </th>
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer" onClick={() => handleSortClick('status')} data-id="element-13394">
                  <div className="flex items-center" data-id="element-13395">
                    Status {renderSortIndicator('status')}
                  </div>
                </th>
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer" onClick={() => handleSortClick('totalValue')} data-id="element-13396">
                  <div className="flex items-center" data-id="element-13397">
                    Value {renderSortIndicator('totalValue')}
                  </div>
                </th>
                <th scope="col" className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-13398">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200" data-id="element-13399">
              {sortedReceipts.length > 0 ? sortedReceipts.map(receipt => <tr key={receipt.id} className="hover:bg-gray-50" data-id="element-13400">
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-indigo-600" data-id="element-13401">
                      {receipt.id}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500" data-id="element-13402">
                      <div className="flex flex-col" data-id="element-13403">
                        <div className="flex items-center" data-id="element-13404">
                          {getPrimaryPO(receipt)}
                        </div>
                        {receipt.purchaseOrders && receipt.purchaseOrders.length > 1 && <div className="text-xs text-gray-400 mt-1 flex items-center" data-id="element-13405">
                              <LinkIcon className="h-3 w-3 mr-1" data-id="element-13406" />
                              {receipt.purchaseOrders.length} POs
                            </div>}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500" data-id="element-13407">
                      <div className="flex flex-col" data-id="element-13408">
                        <div className="flex items-center" data-id="element-13409">
                          {getPrimaryVendor(receipt)}
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500" data-id="element-13410">
                      <div className="flex items-center" data-id="element-13411">
                        <CalendarIcon className="h-4 w-4 mr-1 text-gray-400" data-id="element-13412" />
                        {formatDate(receipt.date, locale)}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500" data-id="element-13413">
                      <div className="flex items-center" data-id="element-13414">
                        <UserIcon className="h-4 w-4 mr-1 text-gray-400" data-id="element-13415" />
                        {receipt.receivedBy}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap" data-id="element-13416">
                      <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${receipt.status === 'Complete' ? 'bg-green-100 text-green-800' : 'bg-yellow-100 text-yellow-800'}`} data-id="element-13417">
                        {receipt.status}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500" data-id="element-13418">
                      {formatCurrency(receipt.totalValue, receipt.currency, locale)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium" data-id="element-13419">
                      <button onClick={() => handleViewReceipt(receipt)} className="text-indigo-600 hover:text-indigo-900 inline-flex items-center" data-id="element-13420">
                        <EyeIcon className="h-4 w-4 mr-1" data-id="element-13421" />
                        View
                      </button>
                    </td>
                  </tr>) : <tr data-id="element-13422">
                  <td colSpan={8} className="px-6 py-4 text-center text-sm text-gray-500" data-id="element-13423">
                    No receipts found
                  </td>
                </tr>}
            </tbody>
          </table>
        </div>
      </div>
      <div className="flex items-center justify-between mt-4 text-sm text-gray-600" data-id="element-13424">
        <div data-id="element-13425">
          Showing {sortedReceipts.length} of {sampleReceipts.length} receipts
        </div>
      </div>
      {/* Receipt Details Modal */}
      {showDetailsModal && selectedReceipt && <div className="fixed z-10 inset-0 overflow-y-auto" data-id="element-13426">
          <div className="flex items-end justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0" data-id="element-13427">
            <div className="fixed inset-0 transition-opacity" aria-hidden="true" data-id="element-13428">
              <div className="absolute inset-0 bg-gray-500 opacity-75" data-id="element-13429"></div>
            </div>
            <span className="hidden sm:inline-block sm:align-middle sm:h-screen" aria-hidden="true" data-id="element-13430">
              &#8203;
            </span>
            <div className="inline-block align-bottom bg-white rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-4xl sm:w-full" data-id="element-13431">
              <div className="bg-white px-4 pt-5 pb-4 sm:p-6 sm:pb-4" data-id="element-13432">
                <div className="flex justify-between items-start pb-3 border-b border-gray-200" data-id="element-13433">
                  <h3 className="text-lg leading-6 font-medium text-gray-900 flex items-center" data-id="element-13434">
                    <TruckIcon className="h-5 w-5 mr-2 text-indigo-500" data-id="element-13435" />
                    Goods Receipt Details - {selectedReceipt.id}
                  </h3>
                  <button onClick={closeDetailsModal} className="bg-white rounded-md text-gray-400 hover:text-gray-500 focus:outline-none" data-id="element-13436">
                    <XIcon className="h-6 w-6" data-id="element-13437" />
                  </button>
                </div>
                <div className="mt-4 grid grid-cols-1 md:grid-cols-2 gap-4" data-id="element-13438">
                  {/* Left Column - Receipt Details */}
                  <div data-id="element-13439">
                    <div className="bg-gray-50 p-4 rounded-md mb-4" data-id="element-13440">
                      <h4 className="text-sm font-medium text-gray-700 mb-3 flex items-center" data-id="element-13441">
                        <FileTextIcon className="h-4 w-4 mr-2 text-indigo-500" data-id="element-13442" />
                        Receipt Information
                      </h4>
                      <div className="grid grid-cols-2 gap-3 text-sm" data-id="element-13443">
                        <div data-id="element-13444">
                          <p className="text-gray-500" data-id="element-13445">Receipt Date:</p>
                          <p className="font-medium" data-id="element-13446">
                            {formatDate(selectedReceipt.date, locale)}
                          </p>
                        </div>
                        <div data-id="element-13447">
                          <p className="text-gray-500" data-id="element-13448">Received By:</p>
                          <p className="font-medium" data-id="element-13449">
                            {selectedReceipt.receivedBy}
                          </p>
                        </div>
                        <div data-id="element-13450">
                          <p className="text-gray-500" data-id="element-13451">Status:</p>
                          <p className="font-medium" data-id="element-13452">
                            <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${selectedReceipt.status === 'Complete' ? 'bg-green-100 text-green-800' : 'bg-yellow-100 text-yellow-800'}`} data-id="element-13453">
                              {selectedReceipt.status}
                            </span>
                          </p>
                        </div>
                        <div data-id="element-13454">
                          <p className="text-gray-500" data-id="element-13455">Entity/Site:</p>
                          <p className="font-medium" data-id="element-13456">
                            {selectedReceipt.entity} / {selectedReceipt.site}
                          </p>
                        </div>
                        <div data-id="element-13457">
                          <p className="text-gray-500" data-id="element-13458">Inspected By:</p>
                          <p className="font-medium" data-id="element-13459">
                            {selectedReceipt.inspectedBy}
                          </p>
                        </div>
                        <div data-id="element-13460">
                          <p className="text-gray-500" data-id="element-13461">Inspection Date:</p>
                          <p className="font-medium" data-id="element-13462">
                            {formatDate(selectedReceipt.inspectionDate, locale)}
                          </p>
                        </div>
                        <div data-id="element-13463">
                          <p className="text-gray-500" data-id="element-13464">Quality Check:</p>
                          <p className="font-medium" data-id="element-13465">
                            {selectedReceipt.qualityCheck}
                          </p>
                        </div>
                        <div data-id="element-13466">
                          <p className="text-gray-500" data-id="element-13467">PO Count:</p>
                          <p className="font-medium" data-id="element-13468">
                            {selectedReceipt.purchaseOrders ? selectedReceipt.purchaseOrders.length : 0}
                          </p>
                        </div>
                      </div>
                    </div>
                    <div className="bg-gray-50 p-4 rounded-md" data-id="element-13469">
                      <h4 className="text-sm font-medium text-gray-700 mb-3 flex items-center" data-id="element-13470">
                        <ShoppingCartIcon className="h-4 w-4 mr-2 text-indigo-500" data-id="element-13471" />
                        Purchase Order Information
                      </h4>
                      {selectedReceipt.purchaseOrders && selectedReceipt.purchaseOrders.map((po, index) => <div key={po.poId} className={`${index > 0 ? 'mt-3 pt-3 border-t border-gray-200' : ''}`} data-id="element-13472">
                            <div className="grid grid-cols-2 gap-3 text-sm" data-id="element-13473">
                              <div data-id="element-13474">
                                <p className="text-gray-500" data-id="element-13475">PO Number:</p>
                                <p className="font-medium text-indigo-600" data-id="element-13476">
                                  {po.poId}
                                </p>
                              </div>
                              <div data-id="element-13477">
                                <p className="text-gray-500" data-id="element-13478">PO Name:</p>
                                <p className="font-medium" data-id="element-13479">{po.poName}</p>
                              </div>
                              <div data-id="element-13480">
                                <p className="text-gray-500" data-id="element-13481">Vendor:</p>
                                <p className="font-medium" data-id="element-13482">{po.vendor}</p>
                              </div>
                              <div data-id="element-13483">
                                <p className="text-gray-500" data-id="element-13484">Currency:</p>
                                <p className="font-medium" data-id="element-13485">
                                  {selectedReceipt.currency}
                                </p>
                              </div>
                            </div>
                          </div>)}
                      <div className="mt-3 pt-3 border-t border-gray-200" data-id="element-13486">
                        <div className="text-sm" data-id="element-13487">
                          <p className="text-gray-500" data-id="element-13488">Delivery Address:</p>
                          <p className="font-medium" data-id="element-13489">
                            {selectedReceipt.deliveryAddress}
                          </p>
                        </div>
                      </div>
                    </div>
                  </div>
                  {/* Right Column - Shipping and Notes */}
                  <div data-id="element-13490">
                    <div className="bg-gray-50 p-4 rounded-md mb-4" data-id="element-13491">
                      <h4 className="text-sm font-medium text-gray-700 mb-3 flex items-center" data-id="element-13492">
                        <TruckIcon className="h-4 w-4 mr-2 text-indigo-500" data-id="element-13493" />
                        Shipping Information
                      </h4>
                      <div className="grid grid-cols-2 gap-3 text-sm" data-id="element-13494">
                        <div data-id="element-13495">
                          <p className="text-gray-500" data-id="element-13496">Carrier:</p>
                          <p className="font-medium" data-id="element-13497">
                            {selectedReceipt.carrierInfo}
                          </p>
                        </div>
                        <div data-id="element-13498">
                          <p className="text-gray-500" data-id="element-13499">Tracking Number:</p>
                          <p className="font-medium" data-id="element-13500">
                            {selectedReceipt.trackingNumber}
                          </p>
                        </div>
                      </div>
                    </div>
                    <div className="bg-gray-50 p-4 rounded-md mb-4" data-id="element-13501">
                      <h4 className="text-sm font-medium text-gray-700 mb-3 flex items-center" data-id="element-13502">
                        <FileTextIcon className="h-4 w-4 mr-2 text-indigo-500" data-id="element-13503" />
                        Notes
                      </h4>
                      <p className="text-sm" data-id="element-13504">{selectedReceipt.notes}</p>
                    </div>
                    <div className="bg-gray-50 p-4 rounded-md" data-id="element-13505">
                      <h4 className="text-sm font-medium text-gray-700 mb-3 flex items-center" data-id="element-13506">
                        <FileTextIcon className="h-4 w-4 mr-2 text-indigo-500" data-id="element-13507" />
                        Delivery Dockets
                      </h4>
                      {selectedReceipt.deliveryDockets && selectedReceipt.deliveryDockets.length > 0 ? <ul className="divide-y divide-gray-200" data-id="element-13508">
                          {selectedReceipt.deliveryDockets.map(docket => <li key={docket.id} className="py-2 flex justify-between" data-id="element-13509">
                              <div className="flex items-center" data-id="element-13510">
                                <FileTextIcon className="h-4 w-4 mr-2 text-gray-400" data-id="element-13511" />
                                <span className="text-sm" data-id="element-13512">{docket.name}</span>
                              </div>
                              <div className="flex items-center" data-id="element-13513">
                                <span className="text-xs text-gray-500 mr-2" data-id="element-13514">
                                  {formatDate(docket.uploadDate, locale)}
                                </span>
                                <button className="text-indigo-600 text-sm hover:text-indigo-800" data-id="element-13515">
                                  View
                                </button>
                              </div>
                            </li>)}
                        </ul> : <p className="text-sm text-gray-500 italic" data-id="element-13516">
                          No delivery dockets attached
                        </p>}
                    </div>
                  </div>
                </div>
                {/* Receipt Line Items */}
                <div className="mt-6" data-id="element-13517">
                  <h4 className="text-sm font-medium text-gray-700 mb-3 flex items-center" data-id="element-13518">
                    <PackageIcon className="h-4 w-4 mr-2 text-indigo-500" data-id="element-13519" />
                    Receipt Line Items
                  </h4>
                  <div className="overflow-x-auto border border-gray-200 rounded-md" data-id="element-13520">
                    <table className="min-w-full divide-y divide-gray-200" data-id="element-13521">
                      <thead className="bg-gray-50" data-id="element-13522">
                        <tr data-id="element-13523">
                          <th scope="col" className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-13524">
                            PO #
                          </th>
                          <th scope="col" className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-13525">
                            Item Code
                          </th>
                          <th scope="col" className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-13526">
                            Description
                          </th>
                          <th scope="col" className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-13527">
                            Ordered
                          </th>
                          <th scope="col" className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-13528">
                            Received
                          </th>
                          <th scope="col" className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-13529">
                            Remaining
                          </th>
                          <th scope="col" className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-13530">
                            Unit Price
                          </th>
                          <th scope="col" className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-13531">
                            Value
                          </th>
                        </tr>
                      </thead>
                      <tbody className="bg-white divide-y divide-gray-200" data-id="element-13532">
                        {getAllItems(selectedReceipt).map(item => <tr key={`${item.poId}-${item.id}`} className="hover:bg-gray-50" data-id="element-13533">
                            <td className="px-3 py-2 whitespace-nowrap text-sm text-indigo-600" data-id="element-13534">
                              {item.poId}
                            </td>
                            <td className="px-3 py-2 whitespace-nowrap text-sm font-medium text-gray-900" data-id="element-13535">
                              {item.itemCode}
                            </td>
                            <td className="px-3 py-2 whitespace-nowrap text-sm text-gray-500" data-id="element-13536">
                              {item.description}
                            </td>
                            <td className="px-3 py-2 whitespace-nowrap text-sm text-gray-500" data-id="element-13537">
                              {item.poQuantity} {item.uom}
                            </td>
                            <td className="px-3 py-2 whitespace-nowrap text-sm text-gray-500" data-id="element-13538">
                              {item.quantity} {item.uom}
                            </td>
                            <td className="px-3 py-2 whitespace-nowrap text-sm text-gray-500" data-id="element-13539">
                              {item.remainingQuantity} {item.uom}
                            </td>
                            <td className="px-3 py-2 whitespace-nowrap text-sm text-gray-500" data-id="element-13540">
                              {formatCurrency(item.unitPrice, selectedReceipt.currency, locale)}
                            </td>
                            <td className="px-3 py-2 whitespace-nowrap text-sm text-gray-500" data-id="element-13541">
                              {formatCurrency(item.value, selectedReceipt.currency, locale)}
                            </td>
                          </tr>)}
                        <tr className="bg-gray-50" data-id="element-13542">
                          <td colSpan="7" className="px-3 py-2 text-right text-sm font-medium text-gray-900" data-id="element-13543">
                            Total Value:
                          </td>
                          <td className="px-3 py-2 whitespace-nowrap text-sm font-medium text-gray-900" data-id="element-13544">
                            {formatCurrency(selectedReceipt.totalValue, selectedReceipt.currency, locale)}
                          </td>
                        </tr>
                      </tbody>
                    </table>
                  </div>
                </div>
                {/* Receipt Status Indicators */}
                <div className="mt-6 flex flex-wrap gap-4" data-id="element-13545">
                  <div className="flex items-center text-sm" data-id="element-13546">
                    <div className={`flex-shrink-0 h-4 w-4 rounded-full ${selectedReceipt.status === 'Complete' ? 'bg-green-500' : 'bg-yellow-500'} mr-1.5`} data-id="element-13547"></div>
                    <p className="text-gray-700" data-id="element-13548">
                      Receipt Status:{' '}
                      <span className="font-medium" data-id="element-13549">
                        {selectedReceipt.status}
                      </span>
                    </p>
                  </div>
                  {selectedReceipt.status === 'Complete' ? <div className="flex items-center text-sm text-green-700" data-id="element-13550">
                      <CheckCircleIcon className="h-4 w-4 mr-1.5" data-id="element-13551" />
                      <p data-id="element-13552">All items received</p>
                    </div> : <div className="flex items-center text-sm text-yellow-700" data-id="element-13553">
                      <ClockIcon className="h-4 w-4 mr-1.5" data-id="element-13554" />
                      <p data-id="element-13555">Partially received - awaiting further deliveries</p>
                    </div>}
                </div>
              </div>
              <div className="bg-gray-50 px-4 py-3 sm:px-6 sm:flex sm:flex-row-reverse" data-id="element-13556">
                <button type="button" onClick={closeDetailsModal} className="w-full inline-flex justify-center rounded-md border border-gray-300 shadow-sm px-4 py-2 bg-white text-base font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 sm:mt-0 sm:ml-3 sm:w-auto sm:text-sm" data-id="element-13557">
                  Close
                </button>
              </div>
            </div>
          </div>
        </div>}
    </div>;
}